import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from '@/config/site';

/**
 * Single shared i18next instance.
 *
 * Initialization is synchronous (we don't await loadResources here) so the
 * app can render immediately — namespace fetches are async; until they
 * resolve, `t(...)` returns the key as a placeholder (suspense disabled).
 *
 * Backend: HttpBackend hits `{API_BASE}/i18n/messages/{lng}/{ns}`, served
 * by the Hono backend. The endpoint merges static file defaults
 * (`functions/locales/{lng}/{ns}.json`) with tenant DB overrides
 * (`text_overrides`) and answers ETag-revalidated 304s for unchanged data.
 *
 * Detection order — URL path > cookie > navigator. The path detector is
 * intentionally first so a shared link in Spanish (e.g., /es/shop) wins
 * over a stale cookie. The fallback chain ends at the tenant's
 * defaultLocale; a non-default locale's missing key falls through to it.
 */
const NAMESPACES = ['common', 'nav'] as const;

// Mirror the base-URL resolution in `web/src/lib/api.ts`. In production the
// frontend (Cloudflare Pages) and backend (api.{tenant}.{tld}) live on
// different origins, so a relative `/api/...` path gets caught by the SPA
// fallback in `_redirects` and returns HTML — i18next then fails to parse
// JSON and `t(...)` falls through to render bare keys.
const API_BASE = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api`
  : '/api';

i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: SUPPORTED_LOCALES,
    nonExplicitSupportedLngs: true,
    load: 'currentOnly',
    ns: NAMESPACES as unknown as string[],
    defaultNS: 'common',
    interpolation: { escapeValue: false }, // React already escapes
    detection: {
      order: ['path', 'cookie', 'navigator'],
      lookupFromPathIndex: 0,
      caches: ['cookie'],
      cookieMinutes: 60 * 24 * 365,
    },
    backend: {
      loadPath: `${API_BASE}/i18n/messages/{{lng}}/{{ns}}`,
    },
    react: {
      useSuspense: false,
    },
  });

export default i18n;
